// extend libraries
import { Crncy } from '@paywithextend/localization'
import { connect, useSelector } from 'react-redux'
import { ExtendCurrency } from 'src/enums'
import { getLocaleInfo } from 'src/localization/locales'
import { RootState } from 'src/redux/createRootReducer'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import validateCurrencyCode from 'validate-currency-code'

interface CurrencyProps {
  /** The amount to format, in dollars */
  amount: number

  /**
   * The currency of the amount.
   *
   * @default userLocale.currency
   * If not provided, defaults to the user's locale-based currency. */
  currency?: ExtendCurrency

  /**
   * The maximum number of fraction digits to display (e.g., cents).
   * @default 2
   * @example 2 // => "$1,234.56"
   */
  maximumFractionDigits?: number

  /**
   * Determines how large numbers are displayed.
   * 'standard' shows full numbers, 'compact' shortens them (e.g., 1K, 1M).
   *
   * @default 'standard'
   * @example 'compact' // => "$1K" instead of "$1,000"
   */
  notation?: 'standard' | 'compact'

  /**
   * Forces hiding the currency code in the output.
   * Useful when the amount and currency are styled differently,
   * or if the currency is labeled elsewhere.
   *
   * If forceHideCurrency and forceShowCurrency are both false, the currency code
   * will be shown only if it differs from the user's locale currency.
   *
   * @default false
   * @example true // => "$1,234.56"
   */
  forceHideCurrency?: boolean

  /**
   * Forces the currency code to be shown alongside the formatted amount,
   * even if it matches the default locale currency.
   * Useful when displaying amounts in multiple currencies.
   *
   * If forceHideCurrency and forceShowCurrency are both false, the currency code
   * will be shown only if it differs from the user's locale currency.
   *
   * @default false
   * @example true // => "$1,234.56 USD"
   */
  forceShowCurrency?: boolean
}

export const useCurrencyFormatter = (): ((args: CurrencyProps) => string) => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const defaultDisplayCurrency = getLocaleInfo(locale).currency
  return (args: CurrencyProps): string => {
    const {
      amount,
      currency = defaultDisplayCurrency,
      maximumFractionDigits = 2,
      notation,
      forceHideCurrency = false,
      forceShowCurrency = false,
    } = args

    const validCurrency = validateCurrencyCode(currency)
      ? currency
      : defaultDisplayCurrency
    const formattedAmount = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: validCurrency,
      maximumFractionDigits: maximumFractionDigits,
      minimumFractionDigits: maximumFractionDigits,
      notation: notation,
      currencyDisplay: 'narrowSymbol',
    }).format(amount)

    if (forceShowCurrency) {
      return formattedAmount + ' ' + validCurrency
    }

    if (forceHideCurrency) {
      return formattedAmount
    }

    return defaultDisplayCurrency !== validCurrency
      ? formattedAmount + ' ' + validCurrency
      : formattedAmount
  }
}

/** @deprecated */
export default connect((state: RootState) => {
  const locale = selectNullSafeLocaleFromState(state)
  return {
    locale: locale,
    defaultDisplayCurrency: getLocaleInfo(locale).currency,
  }
}, {})(Crncy)
